import React from 'react';

import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  SvgIcon,
} from '@mui/material';
import FlightIcon from '@mui/icons-material/Flight';

import FlightSegmentCrewStack from '../../components/FlightSegmentCrewStack';
import FlightSegmentTimesStack from '../../components/FlightSegmentTimesStack';

import { CabinEventReport } from '../../core/api/clients';
import useSaveNewCabinEvent from '../../core/api/hooks/cabin-event/useSaveNewCabinEvent';
import { useAuthorizedFormData } from '../../core/context/AuthorizedFormDataContext';

function CabinEventNewOptions({ data }: { data: CabinEventReport[] }) {
  const [segments, setSegments] = React.useState<CabinEventReport[]>([]);
  const [isOpen, setOpen] = React.useState<boolean>(false);

  const { mutate: saveNewCabinEvent } = useSaveNewCabinEvent();
  const navigate = useNavigate();
  const authorizedFormData = useAuthorizedFormData();

  React.useEffect(() => {
    if (data !== null && data.length > 0) {
      setSegments(data);
      setOpen(true);
    }
  }, [data]);

  const createCabinEvent = (report: CabinEventReport) => {
    saveNewCabinEvent(report, {
      onSuccess: (cabinEventReport: CabinEventReport) => {
        navigate(`${authorizedFormData.metadata?.route}/${cabinEventReport.id}/edit`, {
          replace: true,
        });
      },
    });
  };

  const handleClose = () => setOpen(false);

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle>Multiple Segments Found</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Please select the flight segment for which you want to create a form
        </DialogContentText>
        <List>
          {segments.map((item) => (
            <ListItem
              key={item.createdOn}
              disablePadding
              sx={{ py: 0 }}
            >
              <ListItemButton
                sx={{ padding: 2 }}
                onClick={() => createCabinEvent(item)}
              >
                <ListItemIcon sx={{ ml: 2 }}>
                  <SvgIcon>
                    <FlightIcon />
                  </SvgIcon>
                </ListItemIcon>
                <ListItemText
                  primary={`${item.aircraftNumber} | ${item.departureAirport} - ${item.landingAirport}`}
                  secondary={(
                    <Stack>
                      <FlightSegmentTimesStack
                        pushTime={item.pushTime}
                        outTime={item.outTime}
                        offTime={item.offTime}
                        onTime={item.onTime}
                        inTime={item.inTime}
                      />
                      <FlightSegmentCrewStack
                        captain={item.captain}
                        firstOfficer={item.firstOfficer}
                        flightAttendant1={item.flightAttendant}
                      />
                    </Stack>
                  )}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}

export default CabinEventNewOptions;
